import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import { Flex, rem, ScrollArea, Space, Stack } from '@mantine/core';

import CustomSpinner from '@/components/Spinner.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useRevalidateAutomation from '@/hooks/api/automations/useRevalidateAutomation';
import StepsTree from '@/pages/Editor/components/tree/StepsTree.tsx';
import StaleDraftNotice from '@/pages/Editor/components/StaleDraftNotice.tsx';
import { useStepContext } from '@/context/StepContext/useStepContext.ts';
import AutomationPageWrapper from '@/layouts/AutomationPageWrapper/AutomationPageWrapper.tsx';
import { DataInputIconsProvider } from '@/context/DataInputIconContext/provider.tsx';
import { useAutomation } from '@/context/AutomationContext/useAutomationContext.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import LocalModulesAccordion from '@/pages/Editor/components/LocalSubflows/LocalModulesAccordion.tsx';
import LocalModuleEditor from '@/pages/Editor/components/LocalSubflows/LocalModuleEditor.tsx';
import VersionBlueprintInspector from '@/pages/Editor/components/VersionHistory/VersionBlueprintInspector.tsx';
import VersionComparisonInspector from '@/pages/Editor/components/VersionHistory/VersionComparisonInspector.tsx';

function EditorPane() {
  const automation = useAutomation();

  const {
    data: version,
    isPending: loadingVersion,
    error: latestVersionError,
  } = useRetrieveLatestAutomationVersion();

  const { mutate: revalidateAutomation } = useRevalidateAutomation();
  const hasRevalidated = useRef<string | null>(null);

  useEffect(() => {
    if (automation?.id && hasRevalidated.current !== automation.id) {
      revalidateAutomation({});
      hasRevalidated.current = automation.id;
    }
  }, [automation?.id, revalidateAutomation]);

  const latestVersion = useMemo(() => {
    return loadingVersion || latestVersionError ? undefined : version;
  }, [latestVersionError, loadingVersion, version]);

  const steps = latestVersion?.steps || [];
  const { target, setActiveCompositionTarget } = useActiveCompositionTarget();
  const selectedLocalSubflow = target.scope_kind === 'local_subflow'
    ? latestVersion?.local_subflows?.find((subflow) => subflow.subflow_id === target.subflow_id)
    : undefined;
  const hasMissingLocalSubflow = Boolean(latestVersion)
    && target.scope_kind === 'local_subflow'
    && !selectedLocalSubflow;
  const isLocalSubflowEditor = target.scope_kind === 'local_subflow' && !hasMissingLocalSubflow;

  const { scrollableRef } = useStepContext();

  useEffect(() => {
    if (hasMissingLocalSubflow) {
      void setActiveCompositionTarget({ scope_kind: 'root' }, { replace: true });
    }
  }, [hasMissingLocalSubflow, setActiveCompositionTarget]);

  if (!latestVersion) {
    return (
      <>
        <VersionBlueprintInspector />
        <VersionComparisonInspector />
        <CustomSpinner />
      </>
    );
  }

  return (
    <>
      <VersionBlueprintInspector />
      <VersionComparisonInspector />
      <DataInputIconsProvider>
        <AutomationPageWrapper automation={automation}>
          <ScrollArea w="100%" h="100%" viewportRef={scrollableRef as RefObject<HTMLDivElement>}>
            <Flex w="100%" h="100%" justify="center">
              <Stack
                p="lg"
                align="center"
                style={{ position: 'relative', overflow: 'visible' }}
                w={isLocalSubflowEditor ? 'min(100%, 960px)' : rem(500)}
                gap={10}
                mb="xl"
                pt="xl"
                pb="xl"
              >
                <StaleDraftNotice />
                {isLocalSubflowEditor ? (
                  <LocalModuleEditor />
                ) : (
                  <>
                    <LocalModulesAccordion />
                    <StepsTree steps={steps} indentationWidth={40} />
                  </>
                )}
                <Space h="xl" w="100%" />
              </Stack>
            </Flex>
          </ScrollArea>
        </AutomationPageWrapper>
      </DataInputIconsProvider>
    </>
  );
}

export default EditorPane;
